import { motion, type HTMLMotionProps } from 'framer-motion';
import type { ReactNode } from 'react';
import { cn } from '../lib/cn';
import { springQuick, tapScale } from '../tokens';

type Variant = 'plain' | 'filled' | 'accent';

type IconButtonProps = Omit<HTMLMotionProps<'button'>, 'children'> & {
  /** Lucide icon (or any 24px ReactNode). */
  icon: ReactNode;
  /** Required for screen readers since the button has no text label. */
  label: string;
  variant?: Variant;
  size?: number;
};

const variantClass: Record<Variant, string> = {
  plain: 'bg-transparent text-text-primary',
  filled: 'bg-surface-secondary text-text-primary',
  accent: 'bg-transparent text-accent',
};

export function IconButton({
  icon,
  label,
  variant = 'plain',
  size = 44,
  className,
  disabled,
  ...rest
}: IconButtonProps) {
  return (
    <motion.button
      type="button"
      aria-label={label}
      whileTap={disabled ? undefined : tapScale}
      transition={springQuick}
      disabled={disabled}
      style={{ width: size, height: size }}
      className={cn(
        'inline-flex items-center justify-center rounded-pill shrink-0',
        variantClass[variant],
        disabled && 'opacity-40 pointer-events-none',
        className,
      )}
      {...rest}
    >
      {icon}
    </motion.button>
  );
}
